/**
 * Expected-error filtering for Sentry.
 *
 * The bulk of historical Sentry noise has come from "expected" errors:
 * tRPC procedures throwing `UNAUTHORIZED`/`FORBIDDEN`/`NOT_FOUND`/`BAD_REQUEST`
 * etc. that bubble up through `Sentry.trpcMiddleware` and get auto-captured.
 * These are first-class HTTP responses to the user, not bugs we need to alert
 * on.
 *
 * This module is shared between the server (`@sentry/bun`) and client
 * (`@sentry/react`) Sentry initializers so the two stay in sync.
 */

/**
 * tRPC error codes that we never want to see as Sentry Issues. They all
 * correspond to deliberate, user-visible HTTP responses (4xx) and are not
 * bugs.
 */
export const EXPECTED_TRPC_ERROR_CODES: ReadonlySet<string> = new Set([
  "BAD_REQUEST",
  "UNAUTHORIZED",
  "FORBIDDEN",
  "NOT_FOUND",
  "METHOD_NOT_SUPPORTED",
  "TIMEOUT",
  "CONFLICT",
  "PRECONDITION_FAILED",
  "PAYLOAD_TOO_LARGE",
  "UNSUPPORTED_MEDIA_TYPE",
  "UNPROCESSABLE_CONTENT",
  "TOO_MANY_REQUESTS",
  "CLIENT_CLOSED_REQUEST",
]);

/**
 * Convenience predicate for the most common call shape:
 * `isExpectedTrpcCode(err.data?.code)`. Treats `undefined` as not-expected so
 * that errors without a tRPC code (network failures, etc.) flow through to
 * Sentry.
 */
export function isExpectedTrpcCode(code: string | undefined | null): boolean {
  return code != null && EXPECTED_TRPC_ERROR_CODES.has(code);
}

/**
 * Service-layer error codes that map 1:1 to expected tRPC codes (see
 * `packages/services/common/errors.ts`). Excluded explicitly because callers
 * sometimes capture the underlying `ServiceError` before it gets wrapped.
 */
const EXPECTED_SERVICE_ERROR_CODES: ReadonlySet<string> = new Set([
  "BAD_REQUEST",
  "CONFLICT",
  "FORBIDDEN",
  "NOT_FOUND",
  "UNAUTHORIZED",
]);

/**
 * Brand string set on every `ServiceError` instance. Mirrors
 * `SERVICE_ERROR_BRAND` in `@blis/services/common/errors`. Duplicated as a
 * literal here so the telemetry package stays dependency-free of services.
 */
const SERVICE_ERROR_BRAND = "@blis/service-error";

/**
 * Type guard helpers — kept structural so we don't have to import either the
 * tRPC server package or the services package into the telemetry package
 * (which has to remain dependency-free of higher tiers).
 */
function getCode(value: unknown): string | undefined {
  if (
    value &&
    typeof value === "object" &&
    "code" in value &&
    typeof (value as { code: unknown }).code === "string"
  ) {
    return (value as { code: string }).code;
  }
  return undefined;
}

function getTrpcClientCode(value: unknown): string | undefined {
  if (
    value &&
    typeof value === "object" &&
    "data" in value &&
    (value as { data: unknown }).data &&
    typeof (value as { data: unknown }).data === "object" &&
    "code" in (value as { data: { code?: unknown } }).data &&
    typeof (value as { data: { code: unknown } }).data.code === "string"
  ) {
    return (value as { data: { code: string } }).data.code;
  }
  return undefined;
}

function getName(value: unknown): string | undefined {
  if (
    value &&
    typeof value === "object" &&
    "name" in value &&
    typeof (value as { name: unknown }).name === "string"
  ) {
    return (value as { name: string }).name;
  }
  return undefined;
}

function getCause(value: unknown): unknown {
  if (value && typeof value === "object" && "cause" in value) {
    return (value as { cause: unknown }).cause;
  }
  return undefined;
}

function isBrandedServiceError(value: unknown): boolean {
  return (
    !!value &&
    typeof value === "object" &&
    (value as { __blisServiceError?: unknown }).__blisServiceError ===
      SERVICE_ERROR_BRAND
  );
}

/**
 * Returns true if the error (or anything in its `.cause` chain) is an
 * "expected" client error that we don't want to send to Sentry.
 *
 * Walks the cause chain so we still suppress when a `TRPCError` is wrapped
 * by another error, or vice versa.
 *
 * Detection is structural — we look for a `code` plus either the `TRPCError`
 * marker or the `ServiceError` brand. This avoids relying on `error.name`,
 * which gets minified in client builds and silently breaks subclass
 * detection.
 */
export function isExpectedClientError(error: unknown): boolean {
  let current: unknown = error;
  let depth = 0;
  while (current != null && depth < 10) {
    const name = getName(current);
    const code = getCode(current);
    const trpcCode = getTrpcClientCode(current) ?? code;

    if (
      (name === "TRPCError" || name === "TRPCClientError") &&
      trpcCode &&
      EXPECTED_TRPC_ERROR_CODES.has(trpcCode)
    ) {
      return true;
    }
    if (
      isBrandedServiceError(current) &&
      code &&
      EXPECTED_SERVICE_ERROR_CODES.has(code)
    ) {
      return true;
    }

    current = getCause(current);
    depth++;
  }
  return false;
}

type SentryEventLike = { exception?: unknown };
type SentryEventHintLike = { originalException?: unknown };

/**
 * Sentry `beforeSend` hook. Drops error events whose original exception is an
 * expected client error.
 *
 * We deliberately key off the original exception (passed in via
 * `EventHint.originalException`) rather than the serialized exception values
 * so that wrapped causes are still inspected.
 *
 * Pass directly to `Sentry.init({ beforeSend: dropExpectedErrorsBeforeSend })`.
 */
export function dropExpectedErrorsBeforeSend<E extends SentryEventLike>(
  event: E,
  hint?: SentryEventHintLike,
): E | null {
  if (!event.exception) {
    return event;
  }
  if (
    hint?.originalException &&
    isExpectedClientError(hint.originalException)
  ) {
    return null;
  }
  return event;
}
